<script setup lang="ts">
import { ActionButton } from '@taler/ui'
import type { MaintenanceSchedule, MarkMaintenanceScheduleDoneInput } from '../internal/types.ts'
import { formatDate, formatLocalDate, parseLocalDate, systemClock, todayUtc } from '@taler/domain'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps<{ schedules: readonly MaintenanceSchedule[] }>()
const emit = defineEmits<{
  delete: [scheduleId: string]
  markDone: [scheduleId: string, input: MarkMaintenanceScheduleDoneInput]
}>()

const { t, locale } = useI18n()

const markingDoneId = ref<string | undefined>(undefined)
const doneDate = ref('')
const doneMileageKm = ref('')

function openMarkDone(scheduleId: string): void {
  markingDoneId.value = scheduleId
  doneDate.value = formatLocalDate(todayUtc(systemClock))
  doneMileageKm.value = ''
}

function submitMarkDone(): void {
  if (markingDoneId.value === undefined || doneDate.value === '') return
  const input: MarkMaintenanceScheduleDoneInput = {
    doneDate: parseLocalDate(doneDate.value),
    ...(doneMileageKm.value === '' ? {} : { doneMileageKm: parseInt(doneMileageKm.value, 10) }),
  }
  emit('markDone', markingDoneId.value, input)
  markingDoneId.value = undefined
}

function interval(schedule: MaintenanceSchedule): string {
  const parts: string[] = []
  if (schedule.intervalKm !== undefined) parts.push(`${schedule.intervalKm} km`)
  if (schedule.intervalMonths !== undefined) parts.push(`${schedule.intervalMonths} mo`)
  return parts.join(' / ')
}
</script>

<template>
  <p v-if="schedules.length === 0" class="text-[color:var(--color-text-muted)]">
    {{ t('garage.schedules.empty') }}
  </p>

  <ul v-else class="flex flex-col gap-2">
    <li
      v-for="schedule in schedules"
      :key="schedule.id"
      class="flex flex-col gap-2 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-3 bg-[color:var(--color-surface)]"
    >
      <div class="flex items-center justify-between gap-3">
        <div class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-3)] font-medium text-[color:var(--color-text)]">{{
            schedule.name
          }}</span>
          <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
            interval(schedule)
          }}</span>
          <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
            {{ t('garage.schedules.lastDone') }}:
            {{
              schedule.lastDoneDate
                ? formatDate(schedule.lastDoneDate, locale as 'en' | 'hu')
                : t('garage.schedules.never')
            }}
          </span>
        </div>
        <div class="flex gap-2">
          <button
            type="button"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-2 py-1 text-[length:var(--text-step-1)] text-[color:var(--color-text)]"
            @click="openMarkDone(schedule.id)"
          >
            {{ t('garage.schedules.markDone') }}
          </button>
          <button
            type="button"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-2 py-1 text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
            :aria-label="`${t('garage.schedules.delete')} ${schedule.name}`"
            @click="$emit('delete', schedule.id)"
          >
            {{ t('garage.schedules.delete') }}
          </button>
        </div>
      </div>

      <form
        v-if="markingDoneId === schedule.id"
        class="flex flex-wrap items-end gap-2 border-t border-[color:var(--color-border)] pt-2"
        @submit.prevent="submitMarkDone"
      >
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
            t('garage.schedules.doneDate')
          }}</span>
          <input
            v-model="doneDate"
            type="date"
            required
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
            t('garage.schedules.doneMileage')
          }}</span>
          <input
            v-model="doneMileageKm"
            type="number"
            min="0"
            :placeholder="t('garage.schedules.doneMileagePlaceholder')"
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
          />
        </label>
        <ActionButton type="submit">
          {{ t('garage.schedules.markDone') }}
        </ActionButton>
        <button
          type="button"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
          @click="markingDoneId = undefined"
        >
          {{ t('garage.schedules.cancel') }}
        </button>
      </form>
    </li>
  </ul>
</template>
